import { useState } from 'react';
import { AlertCircle, BookOpen, Plus, Search, Trash2 } from 'lucide-react';
import type { Glosario } from '@/api/installation.api';
import {
  faltaEnRegla,
  faltaEnTermino,
  filtrarEvitar,
  filtrarTerminos,
  resumenDeGlosario,
} from '@/lib/glosario';
import { Button } from '@/components/ui/Button';
import { Card, CardBody, CardHeader, CardTitle, CardDescription } from '@/components/ui/Card';
import { EmptyState } from '@/components/ui/EmptyState';
import { Input } from '@/components/ui/Input';
import { Modal } from '@/components/ui/Modal';

/**
 * Pestaña GLOSARIO (BLOQUE 2).
 *
 * Esto no es documentación: cada fila viaja dentro del prompt del copiloto. Es
 * la diferencia entre "el técnico ponchó" y "el empleado registró su
 * asistencia" —lo segundo lo delata como sistema en la primera frase—.
 *
 * La pantalla lo dice sin rodeos, porque el dueño tiene que entender que
 * escribir acá cambia cómo le habla el agente, no cómo se guarda un dato.
 *
 * Dos cosas cambiaron respecto de la primera versión: las filas a medio llenar
 * se marcan en pantalla (antes se descartaban al guardar, en silencio, y el
 * dueño se quedaba creyendo que las había cargado) y borrar una pregunta antes,
 * con la palabra en el título.
 */

/** Qué se está por borrar. `indice` es la posición en la lista sin filtrar. */
type PorBorrar = { lista: 'terminos' | 'evitar'; indice: number; nombre: string };

export function GlosarioTab({
  value,
  onChange,
}: {
  value: Glosario;
  onChange: (patch: Partial<Glosario>) => void;
}) {
  const terminos = value.terminos ?? [];
  const evitar = value.evitar ?? [];
  const [busqueda, setBusqueda] = useState('');
  const [porBorrar, setPorBorrar] = useState<PorBorrar | null>(null);

  const resumen = resumenDeGlosario(value);
  // Se busca en las dos listas a la vez: para el dueño es un solo vocabulario y
  // no tiene por qué recordar en cuál de las dos cargó la palabra.
  const terminosVisibles = filtrarTerminos(terminos, busqueda).map((t) => ({
    fila: t,
    indice: terminos.indexOf(t),
  }));
  const evitarVisibles = filtrarEvitar(evitar, busqueda).map((e) => ({
    fila: e,
    indice: evitar.indexOf(e),
  }));
  const buscando = busqueda.trim() !== '';

  function confirmarBorrado() {
    if (!porBorrar) return;
    if (porBorrar.lista === 'terminos') {
      onChange({ terminos: terminos.filter((_, j) => j !== porBorrar.indice) });
    } else {
      onChange({ evitar: evitar.filter((_, j) => j !== porBorrar.indice) });
    }
    setPorBorrar(null);
  }

  return (
    <div className="space-y-4">
      <div className="relative w-full max-w-sm">
        <Search className="pointer-events-none absolute left-3 top-[2.15rem] h-4 w-4 text-slate-400" />
        <Input
          label="Buscar palabra"
          className="pl-9"
          placeholder="Busca en las dos listas"
          value={busqueda}
          onChange={(e) => setBusqueda(e.target.value)}
        />
      </div>

      <Card>
        <CardHeader>
          <CardTitle>Cómo habla este negocio</CardTitle>
          <CardDescription>
            Las palabras que usan aquí adentro y qué significan. El asistente interno las usa para
            hablar como habla usted. Lo que escriba acá cambia cómo le contesta, no cómo se guardan
            los datos.
          </CardDescription>
          <Contador
            total={resumen.palabras}
            incompletas={resumen.palabrasIncompletas}
            visibles={buscando ? terminosVisibles.length : null}
            singular="palabra"
            plural="palabras"
          />
        </CardHeader>
        <CardBody className="space-y-3">
          {terminos.length === 0 && (
            <EmptyState
              icon={BookOpen}
              title="Todavía no hay vocabulario propio"
              description="Sin él, el asistente habla genérico, que es justo lo que lo delata como programa. Agregue las palabras de su negocio."
            />
          )}

          {terminos.length > 0 && buscando && terminosVisibles.length === 0 && (
            <p className="rounded-md border border-dashed border-slate-300 p-4 text-sm text-slate-500">
              Ninguna palabra coincide con «{busqueda.trim()}».
            </p>
          )}

          {terminosVisibles.map(({ fila: t, indice }) => {
            const falta = faltaEnTermino(t);
            return (
              <div
                key={indice}
                className={`grid grid-cols-1 gap-2 rounded-md border p-3 sm:grid-cols-[minmax(0,1fr)_minmax(0,2fr)_auto] sm:items-end ${
                  falta ? 'border-amber-300 bg-amber-50/60' : 'border-slate-200'
                }`}
              >
                <label className="block">
                  <span className="mb-1 block text-xs font-medium text-slate-600">La palabra</span>
                  <Input
                    value={t.termino}
                    placeholder="ponchar"
                    onChange={(e) => {
                      const next = [...terminos];
                      next[indice] = { ...t, termino: e.target.value };
                      onChange({ terminos: next });
                    }}
                  />
                </label>

                <label className="block">
                  <span className="mb-1 block text-xs font-medium text-slate-600">
                    Qué significa acá adentro
                  </span>
                  <Input
                    value={t.significa}
                    placeholder="Marcar que llegó al sitio o que terminó el trabajo"
                    onChange={(e) => {
                      const next = [...terminos];
                      next[indice] = { ...t, significa: e.target.value };
                      onChange({ terminos: next });
                    }}
                  />
                </label>

                <Button
                  variant="ghost"
                  size="sm"
                  aria-label={`Quitar ${t.termino || 'la palabra'}`}
                  onClick={() =>
                    setPorBorrar({
                      lista: 'terminos',
                      indice,
                      nombre: t.termino.trim() || 'esta palabra sin nombre',
                    })
                  }
                >
                  <Trash2 className="h-4 w-4 text-rose-500" />
                </Button>

                {falta && <MarcaDeIncompleta texto={falta} />}
              </div>
            );
          })}

          <Button
            variant="secondary"
            size="sm"
            onClick={() => {
              // Se limpia la búsqueda: si no, la fila nueva nace filtrada y
              // parece que el botón no hizo nada.
              setBusqueda('');
              onChange({ terminos: [...terminos, { termino: '', significa: '' }] });
            }}
          >
            <Plus className="mr-1 h-4 w-4" /> Agregar palabra
          </Button>
        </CardBody>
      </Card>

      <Card>
        <CardHeader>
          <CardTitle>Lo que no hay que decir</CardTitle>
          <CardDescription>
            Cada línea necesita el reemplazo: decir "no digas empleado" sin dar la alternativa no le
            sirve de nada al asistente. Las filas sin reemplazo quedan marcadas y no se guardan.
          </CardDescription>
          <Contador
            total={resumen.reglas}
            incompletas={resumen.reglasIncompletas}
            visibles={buscando ? evitarVisibles.length : null}
            singular="regla"
            plural="reglas"
          />
        </CardHeader>
        <CardBody className="space-y-3">
          {evitar.length === 0 && (
            <EmptyState
              icon={BookOpen}
              title="Todavía no hay palabras prohibidas"
              description="Son las que el asistente nunca debe usar, cada una con la que sí. Mientras no haya ninguna, va a hablar con las suyas."
            />
          )}

          {evitar.length > 0 && buscando && evitarVisibles.length === 0 && (
            <p className="rounded-md border border-dashed border-slate-300 p-4 text-sm text-slate-500">
              Ninguna regla coincide con «{busqueda.trim()}».
            </p>
          )}

          {evitarVisibles.map(({ fila: e, indice }) => {
            const falta = faltaEnRegla(e);
            return (
              <div
                key={indice}
                className={`grid grid-cols-1 gap-2 rounded-md border p-3 sm:grid-cols-[minmax(0,1fr)_minmax(0,1fr)_auto] sm:items-end ${
                  falta ? 'border-amber-300 bg-amber-50/60' : 'border-slate-200'
                }`}
              >
                <label className="block">
                  <span className="mb-1 block text-xs font-medium text-slate-600">En vez de…</span>
                  <Input
                    value={e.no}
                    placeholder="el empleado, el operario"
                    onChange={(ev) => {
                      const next = [...evitar];
                      next[indice] = { ...e, no: ev.target.value };
                      onChange({ evitar: next });
                    }}
                  />
                </label>

                <label className="block">
                  <span className="mb-1 block text-xs font-medium text-slate-600">…di</span>
                  <Input
                    value={e.si}
                    placeholder="el técnico"
                    onChange={(ev) => {
                      const next = [...evitar];
                      next[indice] = { ...e, si: ev.target.value };
                      onChange({ evitar: next });
                    }}
                  />
                </label>

                <Button
                  variant="ghost"
                  size="sm"
                  aria-label={`Quitar la regla de ${e.no || 'esta palabra'}`}
                  onClick={() =>
                    setPorBorrar({
                      lista: 'evitar',
                      indice,
                      nombre: e.no.trim() || 'esta regla sin palabra',
                    })
                  }
                >
                  <Trash2 className="h-4 w-4 text-rose-500" />
                </Button>

                {falta && <MarcaDeIncompleta texto={falta} />}
              </div>
            );
          })}

          <Button
            variant="secondary"
            size="sm"
            onClick={() => {
              setBusqueda('');
              onChange({ evitar: [...evitar, { no: '', si: '' }] });
            }}
          >
            <Plus className="mr-1 h-4 w-4" /> Agregar regla
          </Button>
        </CardBody>
      </Card>

      <Modal
        open={porBorrar !== null}
        onClose={() => setPorBorrar(null)}
        title={`¿Quitar ${porBorrar?.nombre ?? ''} del glosario?`}
        description="El asistente deja de usarla en cuanto guardes esta pestaña."
        footer={
          <div className="flex justify-end gap-2">
            <Button variant="ghost" onClick={() => setPorBorrar(null)}>
              Cancelar
            </Button>
            <Button variant="danger" onClick={confirmarBorrado}>
              Quitar
            </Button>
          </div>
        }
      >
        <p className="text-sm text-slate-600 dark:text-slate-300">
          El cambio se aplica cuando toques <strong>Guardar Glosario</strong>.
        </p>
      </Modal>
    </div>
  );
}

/** "3 palabras · 1 sin completar", y cuántas se ven si hay una búsqueda. */
function Contador({
  total,
  incompletas,
  visibles,
  singular,
  plural,
}: {
  total: number;
  incompletas: number;
  /** Cuántas quedan tras filtrar, o null si no se está buscando. */
  visibles: number | null;
  singular: string;
  plural: string;
}) {
  return (
    <p className="text-xs text-slate-500 dark:text-slate-400">
      {visibles === null
        ? `${total} ${total === 1 ? singular : plural}`
        : `${visibles} de ${total} ${total === 1 ? singular : plural}`}
      {incompletas > 0 && (
        <span className="ml-2 text-amber-700 dark:text-amber-400">
          · {incompletas} sin completar
        </span>
      )}
    </p>
  );
}

/** El aviso de que esa fila, tal como está, no se va a guardar. */
function MarcaDeIncompleta({ texto }: { texto: string }) {
  return (
    <p className="flex items-start gap-1.5 text-xs text-amber-800 sm:col-span-full dark:text-amber-300">
      <AlertCircle className="mt-0.5 h-3.5 w-3.5 shrink-0" />
      {texto}
    </p>
  );
}
